/**
 * «Stampa» del ticket lato sito: scarico del PDF e stampa con il browser.
 *
 * - `downloadPdf`: link temporaneo con `download` (nome del server o
 *   `ticket-<numero>.pdf`).
 * - `printPdfBlob`: PDF in un `<iframe>` nascosto e `print()` del browser; se il
 *   browser non lo consente (eccezione, PDF che non si carica entro 15 s) il PDF
 *   si apre in una nuova scheda da stampare con «Stampa» del browser; se anche la
 *   scheda e bloccata resta il link. Partito il ripiego, l'iframe non stampa piu.
 * - Safari (desktop e iOS) e i telefoni/tablet (iOS, Android: l'iframe non stampa
 *   in modo affidabile) vanno direttamente sulla scheda (`printNeedsTab`): la
 *   scheda vuota si apre SUBITO al clic (`openPendingTab`, gesto dell'utente,
 *   altrimenti il blocco dei popup la fermerebbe dopo l'attesa del server) e
 *   riceve il PDF quando e pronto; se il PDF non arriva si chiude
 *   (`closePendingTab`).
 */
import type { ShareableTicket } from './shareTicket';

export type PrintHow = 'pdf' | 'printer';

export type PrintOutcome =
  | { mode: 'dialog' }
  | { mode: 'tab' }
  | { mode: 'blocked'; url: string }
  /** Annullata dall'utente prima che partisse la stampa: nessuna azione. */
  | { mode: 'cancelled' };

export interface PrintPdfOptions {
  /**
   * Scheda aperta al clic con `openPendingTab` (null = bloccata dal browser):
   * il PDF va li, niente iframe. Assente = comportamento in base al browser.
   */
  pendingTab?: Window | null;
  /** true = l'utente ha annullato: niente stampa né scheda. */
  isCancelled?: () => boolean;
}

/** Il PDF resta disponibile (iframe/URL) per la finestra di stampa e la scheda aperta. */
const KEEP_MS = 10 * 60 * 1000;
const LOAD_TIMEOUT_MS = 15000;

export const ticketPrintFilename = (t: ShareableTicket): string =>
  `ticket-${String(t.ticketNumber || t._id || 'ticket').replace(/[^A-Za-z0-9_-]/g, '_')}.pdf`;

/** Safari (desktop e iOS) stampa una pagina bianca dal PDF in un iframe: si va sulla scheda. */
export const isSafariLike = (ua: string): boolean => /^((?!chrome|chromium|android|crios|fxios|edg).)*safari/i.test(ua || '');

/** Telefono o tablet (iOS, Android, altri «Mobile»): l'iframe non stampa in modo affidabile. */
export const isMobileLike = (ua: string): boolean => /android|iphone|ipad|ipod|mobile|silk|kindle/i.test(ua || '');

/** La stampa va su una scheda (Safari o mobile) invece che nell'iframe nascosto. */
export const printNeedsTab = (ua: string): boolean => isSafariLike(ua) || isMobileLike(ua);

export const downloadPdf = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), KEEP_MS);
};

let lastFrame: HTMLIFrameElement | null = null;

const openInTab = (url: string): PrintOutcome => {
  let win: Window | null = null;
  try {
    // Senza `noopener` nelle feature (con quella window.open restituisce sempre
    // null e non si saprebbe se la scheda e stata bloccata): l'opener si toglie dopo.
    win = window.open(url, '_blank');
    if (win) win.opener = null;
  } catch {
    win = null;
  }
  return win ? { mode: 'tab' } : { mode: 'blocked', url };
};

/**
 * Scheda vuota aperta subito, nel gesto dell'utente (clic su «Stampa»), per
 * ricevere il PDF quando il server l'ha preparato. null se il browser la blocca.
 */
export const openPendingTab = (): Window | null => {
  try {
    const win = window.open('', '_blank');
    if (!win) return null;
    win.opener = null;
    try {
      win.document.title = 'Stampa del ticket';
      if (win.document.body) win.document.body.textContent = 'Preparazione del PDF in corso…';
    } catch {
      // documento non accessibile: la scheda resta vuota fino al PDF
    }
    return win;
  } catch {
    return null;
  }
};

/** Chiude la scheda vuota (PDF non riuscito o stampa annullata). */
export const closePendingTab = (win: Window | null | undefined): void => {
  try {
    if (win && !win.closed) win.close();
  } catch {
    // gia chiusa o non accessibile
  }
};

/** PDF nella scheda aperta al clic; scheda assente o chiusa: si prova ad aprirne una (poi resta il link). */
const showInPendingTab = (win: Window | null, url: string): PrintOutcome => {
  if (win && !win.closed) {
    try {
      win.location.href = url;
      return { mode: 'tab' };
    } catch {
      // ripiego qui sotto
    }
  }
  return openInTab(url);
};

/** Stampa il PDF col browser (vedi l'intestazione del file). */
export const printPdfBlob = (
  blob: Blob,
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
  options: PrintPdfOptions = {}
): Promise<PrintOutcome> => {
  const { isCancelled } = options;
  if (isCancelled?.()) {
    closePendingTab(options.pendingTab);
    return Promise.resolve({ mode: 'cancelled' });
  }
  const url = URL.createObjectURL(blob);
  setTimeout(() => URL.revokeObjectURL(url), KEEP_MS);
  if (options.pendingTab !== undefined) return Promise.resolve(showInPendingTab(options.pendingTab, url));
  if (printNeedsTab(ua)) return Promise.resolve(openInTab(url));
  if (lastFrame) {
    lastFrame.remove();
    lastFrame = null;
  }
  return new Promise<PrintOutcome>((resolve) => {
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    frame.title = 'Stampa del ticket';
    // Fuori vista ma "visibile" (display:none impedisce la stampa in alcuni browser).
    Object.assign(frame.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0', opacity: '0' });
    let done = false;
    const finish = (out: PrintOutcome) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      frame.onload = null;
      resolve(out);
    };
    /** Ripiego sulla scheda: una volta sola, mai dopo la stampa né dopo l'annullamento. */
    const fallback = () => {
      if (done) return;
      if (isCancelled?.()) {
        finish({ mode: 'cancelled' });
        return;
      }
      finish(openInTab(url));
    };
    const timer = setTimeout(fallback, LOAD_TIMEOUT_MS);
    frame.onload = () => {
      if (done) return;
      if (isCancelled?.()) {
        finish({ mode: 'cancelled' });
        return;
      }
      try {
        const w = frame.contentWindow;
        if (!w || typeof w.print !== 'function') throw new Error('stampa non disponibile');
        w.focus();
        w.print();
      } catch {
        fallback();
        return;
      }
      finish({ mode: 'dialog' });
    };
    frame.src = url;
    document.body.appendChild(frame);
    lastFrame = frame;
    setTimeout(() => {
      if (lastFrame === frame) lastFrame = null;
      frame.remove();
    }, KEEP_MS);
  });
};
